import React, {
  FormEvent, ReactElement, useState,
} from 'react';
import {
  Button, FormGroup, Heading, Label, LoaderButton, ModalDialogue, Textarea,
} from 'idsk-react-components';
import { MultiSelect } from 'slovlex-idsk-react-components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { SafeParameterSetter } from '../../../model/types/SafeParameterSetter';
import PrispevokVestnika from '../../../model/PrispevokVestnika';
import PrispevokVestnikaStepEnum from '../../../model/PrispevokVestnikaStepEnum';
import useSearchPredpisZbierkyList from '../hook/useSearchPredpisZbierkyList';
import PredpisZbierky from '../../../model/PredpisZbierky';
import CheckDataTable from '../component/CheckDataTable';
import useSearchPrispevokVestnikaInfoList from '../hook/useSearchPrispevokVestnikaInfoList';
import PrispevokVestnikaInfo from '../../../model/PrispevokVestnikaInfo';
import scrollToTop from '../../../function/scrollToTop';
import PredpisSearchModalComponent from '../component/PredpisSearchModal/PredpisSearchModalComponent';
import { fetchPredpisZbierkyList } from '../../../client/external/indexer/predpisZbierkyListClient';
import useOptions from '../../../hooks/useOptions';
import {
  MAX_MULTISELECT_RESULT, SHOW_ALL_MAX_RESULT,
} from '../../../constants/MultiSelectConstant';

interface Props {
  prispevokVestnika: PrispevokVestnika;
  onSubmit: (e: FormEvent<HTMLFormElement>, activeStep: PrispevokVestnikaStepEnum) => void;
  onUpdateField: SafeParameterSetter<PrispevokVestnika>;
  loading: boolean;
  getFieldErrorMessage: (fieldName: string) => string;
  clearStatus: () => void;
  isUpdate?: boolean;
  onChange: (nextStep: PrispevokVestnikaStepEnum) => Promise<void>;
}

const RelatedDataStep3 = ({
  onSubmit,
  onUpdateField,
  prispevokVestnika,
  loading = false,
  clearStatus,
  getFieldErrorMessage,
  isUpdate,
  onChange,
}: Props): ReactElement => {
  const [searchText, setSearchText] = useState('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const [searchTextRusi, setSearchTextRusi] = useState('');
  const [limitRusi, setLimitRusi] = useState(MAX_MULTISELECT_RESULT);
  const [searchTextMeni, setSearchTextMeni] = useState('');
  const [limitMeni, setLimitMeni] = useState(MAX_MULTISELECT_RESULT);
  const options = useOptions();

  const isItemAlreadySelected = (selectedItems, iri):boolean => selectedItems.some((item) => item.iri === iri);

  const updateVybranyPredpis = async (cislo: string, iri: string, fullName: string): Promise<void> => {
    const iriParts = iri.split('#');

    if (!isItemAlreadySelected(prispevokVestnika.vykonavaciePredpisy, iri)) {
      let selectedPredpis: PredpisZbierky;

      await fetchPredpisZbierkyList(cislo, MAX_MULTISELECT_RESULT, options).then((response) => {
        const newData: PredpisZbierky[] = response.data;
        if (iriParts.length !== 2) {
          selectedPredpis = {
            iri,
            popis: newData[0].popis,
            cislo,
            redirectUrl: newData[0].redirectUrl,
          };
        } else {
          selectedPredpis = {
            iri,
            popis: `${fullName} ${newData[0].popis.substring(newData[0].popis.indexOf('-'))}`,
            cislo,
            redirectUrl: `${newData[0].redirectUrl}#${iriParts[1]}`,
          };
        }
      });

      const updatedSelectedItems = [...prispevokVestnika.vykonavaciePredpisy, selectedPredpis];
      onUpdateField('vykonavaciePredpisy', updatedSelectedItems);
      window.parent.postMessage(
        {
          type: 'iframe-to-parent-modal-dialogue-close',
        },
        '*',
      );
    }
  };
  const {
    predpisZbierkyList,
    predpisZbierkyListStatus,
  } = useSearchPredpisZbierkyList(
    searchText,
    limit,
  );
  const {
    prispevokVestnikaInfoList: rusiList,
    prispevokVestnikaInfoListStatus: rusiListStatus,
  } = useSearchPrispevokVestnikaInfoList(
    prispevokVestnika.vestnik,
    searchTextRusi,
    limitRusi,
  );
  const {
    prispevokVestnikaInfoList: meniList,
    prispevokVestnikaInfoListStatus: meniListStatus,
  } = useSearchPrispevokVestnikaInfoList(
    prispevokVestnika.vestnik,
    searchTextMeni,
    limitMeni,
  );

  return (
    <div className="prispevok-vestnika-step-pages">
      <Heading className="page-title" size="xl">
        { isUpdate ? 'Úprava súvisiacich informácií' : 'Vyplňte súvisiace informácie'}
      </Heading>
      <MultiSelect
        loading={predpisZbierkyListStatus.loading}
        handleShowAll={() => {
          setLimit(SHOW_ALL_MAX_RESULT);
        }}
        withShowAllButton={(limit === MAX_MULTISELECT_RESULT
            && predpisZbierkyList.length === MAX_MULTISELECT_RESULT)
          || (limit === SHOW_ALL_MAX_RESULT && predpisZbierkyList.length <= MAX_MULTISELECT_RESULT)}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
          setLimit(MAX_MULTISELECT_RESULT);
        }}
        label="Vykonáva predpisy Zbierky zákonov SR"
        hint="Začnite písať a vyberte z ponúkaných možností."
        itemListAttribute="popis"
        width="three-quarters"
        data={predpisZbierkyList}
        required={false}
        dataAttributesToShow={[{
          key: 'popis',
          label: '',
        }]}
        selectedItemList={prispevokVestnika.vykonavaciePredpisy}
        setSelectedItemList={(selectedItems: PredpisZbierky[]) => {
          onUpdateField('vykonavaciePredpisy', selectedItems);
        }}
        maxResults={SHOW_ALL_MAX_RESULT}
        sort={false}
      >
        <ModalDialogue
          title=""
          openModalDialogueBtn={(
            <Button
              size="large"
              buttonStyle="third"
              onClick={() => {
                window.parent.postMessage({
                  type: 'iframe-to-parent-modal-dialogue-open',
                }, '*');
              }}
            >
              Vybrať predpis
            </Button>
          )}
        >
          <HelmetProvider>
            <Helmet>
              <script src={`${process.env.REACT_APP_SUBPATH}js/jquery.min.js`} type="application/javascript" />
            </Helmet>
            <PredpisSearchModalComponent setUpdateVybranyPredpis={updateVybranyPredpis} />
          </HelmetProvider>
        </ModalDialogue>
      </MultiSelect>
      <MultiSelect
        loading={meniListStatus.loading}
        handleShowAll={() => {
          setLimitMeni(SHOW_ALL_MAX_RESULT);
        }}
        withShowAllButton={(limitMeni === MAX_MULTISELECT_RESULT
            && meniList.length === MAX_MULTISELECT_RESULT)
          || (limitMeni === SHOW_ALL_MAX_RESULT && meniList.length <= MAX_MULTISELECT_RESULT)}
        searchText={searchTextMeni}
        setSearchText={(text) => {
          setSearchTextMeni(text);
          setLimitMeni(MAX_MULTISELECT_RESULT);
        }}
        label="Mení príspevky"
        hint="Začnite písať a vyberte z ponúkaných možností."
        itemListAttribute="menovka"
        width="three-quarters"
        data={prispevokVestnika.uuid ? meniList.filter(meni => meni.uuid !== prispevokVestnika.uuid) : meniList}
        required={false}
        dataAttributesToShow={[{
          key: 'menovka',
          label: '',
        }]}
        selectedItemList={prispevokVestnika.meniPrispevky}
        setSelectedItemList={(selectedItems: PrispevokVestnikaInfo[]) => {
          onUpdateField('meniPrispevky', selectedItems);
        }}
        sort={false}
        maxResults={SHOW_ALL_MAX_RESULT}
      />
      <MultiSelect
        loading={rusiListStatus.loading}
        handleShowAll={() => {
          setLimitRusi(SHOW_ALL_MAX_RESULT);
        }}
        withShowAllButton={(limitRusi === MAX_MULTISELECT_RESULT
            && rusiList.length === MAX_MULTISELECT_RESULT)
          || (limitRusi === SHOW_ALL_MAX_RESULT && rusiList.length <= MAX_MULTISELECT_RESULT)}
        searchText={searchTextRusi}
        setSearchText={(text) => {
          setSearchTextRusi(text);
          setLimitRusi(MAX_MULTISELECT_RESULT);
        }}
        label="Ruší príspevky"
        hint="Začnite písať a vyberte z ponúkaných možností."
        itemListAttribute="menovka"
        width="three-quarters"
        data={prispevokVestnika.uuid ? rusiList.filter(rusi => rusi.uuid !== prispevokVestnika.uuid) : rusiList}
        required={false}
        dataAttributesToShow={[{
          key: 'menovka',
          label: '',
        }]}
        selectedItemList={prispevokVestnika.rusiPrispevky}
        setSelectedItemList={(selectedItems: PrispevokVestnikaInfo[]) => {
          onUpdateField('rusiPrispevky', selectedItems);
        }}
        sort={false}
        maxResults={SHOW_ALL_MAX_RESULT}
      />
      <FormGroup error={!!getFieldErrorMessage('popis')}>
        <Label htmlFor="popis">Popis <span className="idsk-hint optional-message">(nepovinný údaj)</span></Label>
        <Textarea
          className="idsk-!-width-three-quarters"
          showRemaining
          rows={4}
          expandHeight
          id="popis"
          type="text"
          maxLength={500}
          name="popis"
          value={prispevokVestnika.popis}
          onChange={({ target }) => onUpdateField('popis', target.value)}
          error={getFieldErrorMessage('popis')}
          autoComplete="off"
        />
      </FormGroup>
      <LoaderButton
        loading={loading}
        type="submit"
        size="large"
        onClick={(e) => onSubmit(e, PrispevokVestnikaStepEnum.ATTACHMENTS_4)}
      >Pokračovať
      </LoaderButton>
      <Button
        className="prispevok-vestnika-step-pages__back-button"
        type="button"
        buttonStyle="third"
        size="large"
        disabled={loading}
        onClick={() => {
          clearStatus();
          onUpdateField('activeStep', PrispevokVestnikaStepEnum.DATES_2);
          scrollToTop();
        }}
      >
        Späť
      </Button>
      <Heading size="m">Vaše odpovede</Heading>
      <CheckDataTable
        prispevokVestnika={prispevokVestnika}
        onChange={onChange}
        isUpdate={isUpdate}
      />
    </div>
  );
};

export default RelatedDataStep3;
